import { useCallback, useEffect, useRef, useState } from 'react';

import { failedSlot, previewChart } from '../services/charts';

/** How long a question holds still before the builder asks for its preview (plan §6.2: debounced, never per keystroke). */
export const PREVIEW_DELAY_MS = 600;
/** How many times a refused preview (429, 503) is asked again by itself before it waits for the person. */
export const MAX_AUTO_RETRIES = 3;
// The wait when a refusal names none: the busy server says 5 s; the rate
// limit's window is a minute.
const DEFAULT_WAIT_S = { 429: 10, 503: 5 };

/**
 * The chart builder's preview (charts A5b, plan §6.2): runs the unsaved spec
 * through `POST /api/collections/{id}/charts/preview` and keeps its answer.
 *
 * The preview is rate-limited and gated on the server (keepr-api SCHEMA
 * "Charts" › the preview: 20 a minute per person, ONE in flight per person —
 * 429 `budget: 'inFlight'` — and 503 `charts_busy` when every slot of the
 * process is taken), so it is asked sparingly:
 *
 * - **Debounced.** A spec that holds still for `delay` ms is asked; typing a
 *   label or a goal asks once, after the typing.
 * - **One at a time.** A change while a preview is out never sends a second
 *   request beside it (aborting the first would not free the server's slot,
 *   and the second would be refused `inFlight`): the change waits, and the
 *   newest spec is asked as soon as the answer lands. An answer to a spec
 *   that has since changed is dropped, never shown.
 * - **A refusal waits.** A 429 or 503 is the failed slot the chart's frame
 *   says in its own words (services/charts `failedSlot`, the frame's
 *   `errorSentence`), and the preview asks again by itself after the wait the
 *   server named — at most MAX_AUTO_RETRIES times for one spec, then it waits
 *   for the person (`run`).
 * - **A spec refusal is not a chart.** A 400 (`code`, `message`, `path`) is
 *   `refusal`, which the builder shows on the field its path names; the
 *   frame shows no chart for it.
 *
 *   collectionId  the chart's collection
 *   spec          the spec to preview, or null while the question is incomplete
 *   enabled       false asks nothing (no access; the card cache still loading)
 *
 * Returns { result, warnings, previewSpec, refusal, loading, coolUntil, answeredKey, current, run }:
 * `result` is the run's slot (`{ ok: true, … }` or a failed one), `warnings`
 * what the chart will say about itself, `previewSpec` the spec as the server
 * would store it, `coolUntil` the time (ms) before which a refused preview
 * will not be asked again, `answeredKey` the question (collection and spec)
 * those answers are for, `current` whether that is the question on screen —
 * a caller shows a refusal or a warning only then — and `run()` asks now.
 */
export default function useChartPreview({ collectionId, spec, enabled = true, delay = PREVIEW_DELAY_MS, elementChoices = null }) {
	// The question is the spec IN its collection: the same spec elsewhere is
	// another question, never answered by this one's (the A5 review's L-5a).
	// The person's picks for an element the filter could mean several of
	// (element ids step 3) are part of it: a pick asks again.
	const picks = elementChoices && Object.keys(elementChoices).length ? elementChoices : null;
	const key = spec && collectionId ? JSON.stringify(picks ? [collectionId, spec, picks] : [collectionId, spec]) : null;
	const [result, setResult] = useState(null);
	const [warnings, setWarnings] = useState([]);
	const [previewSpec, setPreviewSpec] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [inFlight, setInFlight] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const [coolUntil, setCoolUntil] = useState(null);
	// The spec (as its key) whose answer — a result, a refusal — is held now.
	const [answeredKey, setAnsweredKey] = useState(null);

	const latest = useRef({ key: null, spec: null, collectionId: null, enabled: false, picks: null });
	latest.current = { key, spec, collectionId, enabled: Boolean(enabled), picks };
	const flying = useRef(false);
	const queued = useRef(false);
	const cool = useRef(0);
	const retries = useRef({ key: null, count: 0 });
	const timer = useRef(null);
	const retryTimer = useRef(null);
	const controller = useRef(null);
	const alive = useRef(true);
	const sendRef = useRef(null);
	const answered = useRef(null);
	// The question whose automatic retries are spent: it waits for Refresh.
	const exhausted = useRef(null);

	const clearTimer = (ref) => {
		if (ref.current) {
			clearTimeout(ref.current);
			ref.current = null;
		}
	};

	const send = useCallback(async ({ manual = false, waited = false } = {}) => {
		const now = latest.current;
		// A closed gate asks nothing, a retry it scheduled included (L-5b).
		if (!alive.current || !now.key || !now.collectionId || !now.enabled) return;
		if (flying.current) {
			// Asked again once the answer lands; Refresh stays a Refresh.
			queued.current = manual ? 'manual' : (queued.current || 'auto');
			return;
		}
		// A send at the end of the server's wait is past it, whatever the clock
		// says a millisecond early; only the automatic asks wait.
		if (!manual && !waited && cool.current > Date.now()) return;
		// A spec already answered is not asked again by itself (Refresh asks).
		if (!manual && answered.current === now.key) {
			setWaiting(false);
			return;
		}
		clearTimer(timer);
		clearTimer(retryTimer);
		setWaiting(false);
		flying.current = true;
		setInFlight(true);
		const sentKey = now.key;
		const abort = typeof AbortController === 'function' ? new AbortController() : null;
		controller.current = abort;
		try {
			const options = { ...(abort ? { signal: abort.signal } : {}), ...(now.picks ? { elementChoices: now.picks } : {}) };
			const data = await previewChart(now.collectionId, now.spec, options);
			if (!alive.current || latest.current.key !== sentKey) return;
			retries.current = { key: null, count: 0 };
			exhausted.current = null;
			cool.current = 0;
			setCoolUntil(null);
			setRefusal(null);
			setResult((data && data.result) || failedSlot(null));
			setWarnings(Array.isArray(data && data.warnings) ? data.warnings : []);
			setPreviewSpec((data && data.spec) || null);
			answered.current = sentKey;
			setAnsweredKey(sentKey);
		} catch (e) {
			if (!alive.current || (abort && abort.signal.aborted) || latest.current.key !== sentKey) return;
			const status = e && e.response ? e.response.status : null;
			const body = (e && e.response && e.response.data) || {};
			if (status === 400 && typeof body.code === 'string') {
				setRefusal({ ...body, code: body.code, message: typeof body.message === 'string' ? body.message : '', path: typeof body.path === 'string' ? body.path : '' });
				setResult(null);
				setWarnings([]);
				answered.current = sentKey;
				setAnsweredKey(sentKey);
				return;
			}
			const slot = failedSlot(e);
			setRefusal(null);
			setResult(slot);
			setWarnings([]);
			// A refused or failed REQUEST is no answer to the spec: asking it again is right.
			answered.current = null;
			setAnsweredKey(sentKey);
			if (status === 429 || status === 503) {
				const named = slot.error && typeof slot.error.retryAfter === 'number' ? slot.error.retryAfter : null;
				const wait = Math.max(1, Math.ceil(named !== null ? named : DEFAULT_WAIT_S[status]));
				cool.current = Date.now() + wait * 1000;
				setCoolUntil(cool.current);
				const tries = retries.current.key === sentKey ? retries.current.count : 0;
				if (tries >= MAX_AUTO_RETRIES) exhausted.current = sentKey;
				if (tries < MAX_AUTO_RETRIES) {
					retries.current = { key: sentKey, count: tries + 1 };
					clearTimer(retryTimer);
					setWaiting(true);
					retryTimer.current = setTimeout(() => {
						retryTimer.current = null;
						if (sendRef.current) sendRef.current({ manual: true });
					}, wait * 1000);
				}
			}
		} finally {
			if (controller.current === abort) controller.current = null;
			flying.current = false;
			if (alive.current) {
				setInFlight(false);
				// The spec changed while this one was out: ask the newest now.
				const next = latest.current;
				const asked = queued.current;
				if (asked || (next.key && next.key !== sentKey && next.enabled)) {
					queued.current = false;
					if (next.key && next.enabled && cool.current <= Date.now()) {
						setWaiting(true);
						clearTimer(timer);
						timer.current = setTimeout(() => {
							timer.current = null;
							if (sendRef.current) sendRef.current({ manual: asked === 'manual' });
						}, 0);
					}
				}
			}
		}
	}, []);
	sendRef.current = send;

	// A changed spec, held still for `delay`, is asked.
	useEffect(() => {
		clearTimer(timer);
		if (!enabled || !key || !collectionId) {
			// A retry set while the gate was open does not go out once it closes.
			if (!enabled) clearTimer(retryTimer);
			setWaiting(false);
			return undefined;
		}
		if (retries.current.key !== key) retries.current = { key: null, count: 0 };
		setWaiting(true);
		timer.current = setTimeout(() => {
			timer.current = null;
			if (cool.current > Date.now()) {
				// The server asked for a wait. A retry already scheduled asks the
				// newest spec when it ends; once the automatic retries are spent,
				// a spec changed meanwhile is still asked when the wait is over
				// (the A5 review's L-5c) — the same spec waits for Refresh.
				if (!retryTimer.current) {
					retryTimer.current = setTimeout(() => {
						retryTimer.current = null;
						if (latest.current.key !== exhausted.current && sendRef.current) sendRef.current({ waited: true });
					}, Math.max(0, cool.current - Date.now()) + 1);
				}
				return;
			}
			send();
		}, delay);
		return () => clearTimer(timer);
	}, [key, enabled, collectionId, delay, send]);

	useEffect(() => {
		alive.current = true;
		return () => {
			alive.current = false;
			clearTimer(timer);
			clearTimer(retryTimer);
			if (controller.current) controller.current.abort();
		};
	}, []);

	const run = useCallback(() => {
		if (cool.current > Date.now()) cool.current = 0;
		retries.current = { key: null, count: 0 };
		exhausted.current = null;
		setCoolUntil(null);
		send({ manual: true });
	}, [send]);

	return {
		result,
		warnings,
		previewSpec,
		refusal,
		loading: inFlight || (waiting && Boolean(key)),
		inFlight,
		coolUntil,
		answeredKey,
		// Whether what is held answers the question on screen now.
		current: Boolean(key) && answeredKey === key,
		run,
	};
}
